import { useState } from "react";
import LoginModal from "@/components/LoginModal.jsx";
import { useAuth } from "@/auth/AuthContext.jsx";
import { useFavorites } from "@/favorites/FavoritesContext.jsx";
import { ROLES } from "@/utils/constants.js";
import "./favorite-heart.css";

/**
 * Heart toggle for Favoris (catalog overlay or product detail toolbar).
 * @param {{ productId: string, className?: string, onLoginRequired?: () => void, variant?: "overlay"|"toolbar" }} props
 */
export default function FavoriteHeartButton({ productId, className = "", onLoginRequired, variant = "overlay" }) {
    const { isAuthenticated, role } = useAuth();
    const { isFavorite, toggleFavorite, addFavorite } = useFavorites();
    const [busy, setBusy] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const active = isAuthenticated && role === ROLES.CUSTOMER && isFavorite(productId);
    const isToolbar = variant === "toolbar";
    const ariaLabel = active ? "Retirer des favoris" : "Ajouter aux favoris";
    const toolbarLabel = "J'aime";

    const handleClick = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isAuthenticated) {
            if (onLoginRequired) {
                onLoginRequired();
            } else {
                setShowLogin(true);
            }
            return;
        }
        if (role !== ROLES.CUSTOMER) {
            return;
        }
        setBusy(true);
        try {
            // One-click add when empty; toggle when already favorited (allows remove from card).
            if (active) {
                await toggleFavorite(productId);
            } else {
                await addFavorite(productId);
            }
        } catch {
            // Context / API errors surface via toast only on success path; ignore here
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button
                type="button"
                className={[
                    "favorite-heart-btn",
                    active ? "is-active" : "",
                    isToolbar ? "favorite-heart-btn--toolbar btn btn-secondary" : "",
                    className
                ].filter(Boolean).join(" ")}
                aria-label={isToolbar ? (active ? "Ne plus aimer" : toolbarLabel) : ariaLabel}
                aria-pressed={active}
                disabled={busy}
                onClick={handleClick}
            >
                {isToolbar && (
                    <span className="product-page-action-label">{toolbarLabel}</span>
                )}
                <span className="favorite-heart-btn__icon" aria-hidden="true">
                    {active ? "♥" : "♡"}
                </span>
            </button>
            {showLogin && (
                <LoginModal
                    initialMode="login"
                    onClose={() => setShowLogin(false)}
                />
            )}
        </>
    );
}

export function FavoritesFeedbackToast() {
    const { feedback } = useFavorites();
    if (!feedback) {
        return null;
    }
    return (
        <div className="favorite-feedback-toast" role="status" aria-live="polite">
            {feedback}
        </div>
    );
}
